/**
 * Ritmo do periodo e economia unitaria: o que um mes renderia neste ritmo
 * e quanto custa cada venda recuperada.
 *
 * Decisoes nao obvias:
 *  1. A projecao e linear e inteira (`projetar`): comissao do periodo /
 *     dias x 30. O rodape avisa que nao considera sazonalidade. Uma projecao
 *     "inteligente" que ninguem confere de cabeca e pior que uma simples que
 *     qualquer pessoa refaz na calculadora.
 *  2. A barra e MARGEM SOBRE COMISSAO, nao sobre recuperado: o Converte
 *     Facil so recebe a comissao, entao e sobre ela que o custo pesa. Tom
 *     bom acima de 60%, atencao entre 30% e 60%, critico abaixo.
 *  3. Economia unitaria divide centavos por contagem e arredonda para
 *     centavo inteiro -- exibicao, nunca base de fatura. Sem venda no
 *     periodo, o texto diz isso em vez de mostrar "R$ 0,00 por venda".
 *  4. Custo por venda cair e bom ("menor_melhor"); comissao e margem por
 *     venda subir e bom.
 */

import { Badge, Footnote, ProgressBar, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import { porUnidade, projetar } from '../comparar';
import type { PlatformTotals } from '../types';
import { Delta, type SentidoDoDelta } from './Delta';

const DIAS_DO_MES = 30;
const MARGEM_BOA_BPS = 6_000;
const MARGEM_ATENCAO_BPS = 3_000;

function tomDaMargem(bps: number | null): 'good' | 'warning' | 'critical' | 'neutral' {
  if (bps === null) {
    return 'neutral';
  }
  if (bps >= MARGEM_BOA_BPS) {
    return 'good';
  }
  if (bps >= MARGEM_ATENCAO_BPS) {
    return 'warning';
  }
  return 'critical';
}

function Unitario({
  rotulo,
  atual,
  anterior,
  sentido,
  vazio,
}: {
  readonly rotulo: string;
  readonly atual: number | null;
  readonly anterior: number | null;
  readonly sentido: SentidoDoDelta;
  readonly vazio: string;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-xl border border-line bg-elevated px-3 py-2.5">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{rotulo}</dt>
      <dd className="text-lg font-semibold tabular-nums text-ink">{atual === null ? VALOR_AUSENTE : formatBRLFromCents(atual)}</dd>
      <dd className="text-xs">{atual === null ? <span className="text-ink-subtle">{vazio}</span> : <Delta atual={atual} anterior={anterior} tipo="dinheiro" sentido={sentido} compacto />}</dd>
    </div>
  );
}

export function RitmoEEconomia({
  totals,
  anterior,
  dias,
  className = '',
}: {
  readonly totals: PlatformTotals;
  readonly anterior: PlatformTotals | null;
  /** Dias cobertos pela janela do cabecalho. */
  readonly dias: number;
  readonly className?: string;
}): JSX.Element {
  // Custo/margem `null` = nao medido (motor real): nada de projetar nem
  // dividir zero; cada cartao diz "custo não medido".
  const semCusto = totals.costCents === null || totals.marginCents === null;
  const projetarOuNulo = (cents: number | null): number | null => (cents === null ? null : projetar(cents, dias, DIAS_DO_MES));
  const porUnidadeOuNulo = (cents: number | null, unidades: number): number | null => (cents === null ? null : porUnidade(cents, unidades));

  const comissaoMes = projetar(totals.commissionCents, dias, DIAS_DO_MES);
  const margemMes = projetarOuNulo(totals.marginCents);
  const custoMes = projetarOuNulo(totals.costCents);
  const tom = semCusto ? 'neutral' : tomDaMargem(totals.marginBps);

  const custoPorVenda = porUnidadeOuNulo(totals.costCents, totals.recoveredCount);
  const comissaoPorVenda = porUnidade(totals.commissionCents, totals.recoveredCount);
  const margemPorVenda = porUnidadeOuNulo(totals.marginCents, totals.recoveredCount);
  const custoPorEntrada = porUnidadeOuNulo(totals.costCents, totals.abandonedCount);

  const ant = anterior;
  const antCustoPorVenda = ant === null ? null : porUnidadeOuNulo(ant.costCents, ant.recoveredCount);
  const antComissaoPorVenda = ant === null ? null : porUnidade(ant.commissionCents, ant.recoveredCount);
  const antMargemPorVenda = ant === null ? null : porUnidadeOuNulo(ant.marginCents, ant.recoveredCount);
  const antCustoPorEntrada = ant === null ? null : porUnidadeOuNulo(ant.costCents, ant.abandonedCount);
  const vazioDeCusto = (semUnidade: string): string => (semCusto ? 'custo ainda não medido' : semUnidade);

  return (
    <SectionCard
      id="ritmo"
      title="Ritmo e economia unitária"
      icon="relogio"
      description="No ritmo deste período, o que um mês inteiro renderia — e quanto custa cada venda recuperada."
      aside={
        <Badge tone={tom === 'neutral' ? 'neutral' : tom} dot>
          {semCusto ? 'Custo não medido' : totals.marginBps === null ? 'Sem comissão' : `Margem ${formatPercentFromBps(totals.marginBps)}`}
        </Badge>
      }
      className={className}
    >
      <div className="flex h-full flex-col justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Um mês neste ritmo</p>
          {comissaoMes !== null && (margemMes === null || custoMes === null) ? (
            <>
              <p className="mt-0.5 text-3xl font-semibold tracking-tight tabular-nums text-ink">
                <span aria-hidden="true">≈ </span>
                <span className="sr-only">aproximadamente </span>
                {formatBRLFromCents(comissaoMes)} <span className="text-base font-medium text-ink-muted">de comissão</span>
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                Custos e margem ainda não medidos · base de {formatCount(dias)} {dias === 1 ? 'dia' : 'dias'}
              </p>
            </>
          ) : comissaoMes === null || margemMes === null || custoMes === null ? (
            <p className="mt-1 text-sm text-ink-subtle">Ainda não há dias suficientes para projetar.</p>
          ) : (
            <>
              <p className="mt-0.5 text-3xl font-semibold tracking-tight tabular-nums text-ink">
                <span aria-hidden="true">≈ </span>
                <span className="sr-only">aproximadamente </span>
                {formatBRLFromCents(comissaoMes)} <span className="text-base font-medium text-ink-muted">de comissão</span>
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                ≈ {formatBRLFromCents(custoMes)} de custos · ≈ <strong className="font-semibold text-ink">{formatBRLFromCents(margemMes)}</strong> de margem · base de{' '}
                {formatCount(dias)} {dias === 1 ? 'dia' : 'dias'}
              </p>
            </>
          )}
        </div>

        {totals.costCents === null || totals.marginCents === null ? (
          <p className="text-xs text-ink-muted">
            Margem sobre a comissão: <strong className="font-semibold text-ink">{VALOR_AUSENTE}</strong> · o motor ainda não lê as faturas de voz, WhatsApp e infra.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-xs text-ink-muted">
              <span>
                Margem sobre a comissão:{' '}
                <strong className="font-semibold tabular-nums text-ink">{totals.marginBps === null ? VALOR_AUSENTE : formatPercentFromBps(totals.marginBps)}</strong>
              </span>
              <span className="tabular-nums">
                {formatBRLFromCents(totals.costCents)} de {formatBRLFromCents(totals.commissionCents)}
              </span>
            </div>
            <ProgressBar value={Math.max(0, totals.marginCents)} max={Math.max(1, totals.commissionCents)} tone={tom} label="Margem sobre a comissão do período" />
          </div>
        )}

        <dl className="grid grid-cols-2 gap-2">
          <Unitario rotulo="Custo por venda recuperada" atual={custoPorVenda} anterior={antCustoPorVenda} sentido="menor_melhor" vazio={vazioDeCusto('sem venda no período')} />
          <Unitario rotulo="Comissão por venda" atual={comissaoPorVenda} anterior={antComissaoPorVenda} sentido="maior_melhor" vazio="sem venda no período" />
          <Unitario rotulo="Margem por venda" atual={margemPorVenda} anterior={antMargemPorVenda} sentido="maior_melhor" vazio={vazioDeCusto('sem venda no período')} />
          <Unitario rotulo="Custo por entrada" atual={custoPorEntrada} anterior={antCustoPorEntrada} sentido="menor_melhor" vazio={vazioDeCusto('sem entrada no período')} />
        </dl>

        <Footnote>
          Projeção linear: comissão do período ÷ dias × 30, sem sazonalidade.{' '}
          {semCusto ? 'O custo da operação ainda não é medido pelo motor.' : 'Custos com tarifas de exemplo; as reais vêm das faturas.'} Cada venda aqui é de quem já tinha
          abandonado ou vencido.
        </Footnote>
      </div>
    </SectionCard>
  );
}
